<template>
  <div class="profile-dropdown-wrapper">
    <VaDropdown v-model="isShown" :offset="[9, 0]" class="profile-dropdown" stick-to-edges>
      <template #anchor>
        <VaButton preset="secondary" color="textPrimary" class="profile-button">
          <span class="profile-dropdown__anchor min-w-max flex items-center gap-2">
            <slot />
            <VaAvatar :size="36" class="profile-avatar">
              <span class="avatar-text">{{ userInitials }}</span>
            </VaAvatar>
          </span>
        </VaButton>
      </template>
      <VaDropdownContent
        class="profile-dropdown__content md:w-64 px-0 py-0 w-full"
        :style="{ '--hover-color': hoverColor }"
      >
        <!-- User Info Header -->
        <div class="user-info-header px-4 py-4 border-b border-gray-200">
          <div class="flex items-center gap-3">
            <VaAvatar :size="48" class="profile-avatar-large">
              <span class="avatar-text-large">{{ userInitials }}</span>
            </VaAvatar>
            <div class="flex-1 min-w-0">
              <div class="font-semibold text-base truncate">{{ userStore.userName }}</div>
              <div class="text-sm text-gray-600 truncate">{{ userStore.email }}</div>
            </div>
          </div>
        </div>

        <VaList v-for="group in options" :key="group.name" class="py-2">
          <header v-if="group.name" class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs px-4">
            {{ t(`user.${group.name}`) }}
          </header>
          <VaListItem
            v-for="item in group.list"
            :key="item.name"
            class="menu-item px-4 text-base cursor-pointer h-8"
            v-bind="resolveLinkAttribute(item)"
            @click="handleItemClick(item)"
          >
            <VaIcon :name="item.icon" class="pr-1" color="secondary" />
            {{ t(`user.${item.name}`) }}
          </VaListItem>
          <VaListSeparator v-if="group.separator" class="mx-3 my-2" />
        </VaList>
      </VaDropdownContent>
    </VaDropdown>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import { useRouter } from 'vue-router'
import { useUserStore } from '../../../../stores/user-store'

const { colors, setHSLAColor } = useColors()
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))

const { t } = useI18n()
const router = useRouter()
const userStore = useUserStore()

// Compute user initials from name or email
const userInitials = computed(() => {
  const name = userStore.userName || userStore.email
  if (!name || name === 'anonymous') return 'U'

  // If email, use first letter
  if (name.includes('@')) {
    return name.charAt(0).toUpperCase()
  }

  // If name with spaces, use first letter of each word
  const parts = name.split(' ').filter((p) => p.length > 0)
  if (parts.length >= 2) {
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase()
  }

  // Otherwise use first two letters
  return name.substring(0, 2).toUpperCase()
})

onMounted(async () => {
  // Fetch user profile if not already loaded
  if (!userStore.userId) {
    try {
      await userStore.fetchUserProfile()
    } catch (error) {
      console.error('Failed to load user profile:', error)
    }
  }
})

type ProfileListItem = {
  name: string
  to?: string
  href?: string
  icon: string
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

withDefaults(
  defineProps<{
    options?: ProfileOptions[]
  }>(),
  {
    options: () => [
      {
        name: 'account',
        separator: true,
        list: [
          {
            name: 'profile',
            to: 'preferences',
            icon: 'mso-account_circle',
          },
          // {
          //   name: 'settings',
          //   to: 'settings',
          //   icon: 'mso-settings',
          // },
          // {
          //   name: 'billing',
          //   to: 'billing',
          //   icon: 'mso-receipt_long',
          // },
          // {
          //   name: 'projects',
          //   to: 'projects',
          //   icon: 'mso-favorite',
          // },
        ],
      },
      // {
      //   name: 'explore',
      //   separator: true,
      //   list: [
      //     {
      //       name: 'faq',
      //       to: 'faq',
      //       icon: 'mso-quiz',
      //     },
      //     {
      //       name: 'helpAndSupport',
      //       href: 'https://discord.gg/u7fQdqQt8c',
      //       icon: 'mso-error',
      //     },
      //   ],
      // },
      {
        name: '',
        separator: false,
        list: [
          {
            name: 'logout',
            to: 'login',
            icon: 'mso-logout',
          },
        ],
      },
    ],
  },
)

const isShown = ref(false)

const resolveLinkAttribute = (item: ProfileListItem) => {
  return item.to ? { to: { name: item.to } } : item.href ? { href: item.href, target: '_blank' } : {}
}

const handleItemClick = (item: ProfileListItem) => {
  if (item.name === 'logout') {
    sessionStorage.removeItem('access_token')
    router.push({ name: 'login' })
  }
}
</script>

<style lang="scss" scoped>
.profile-dropdown {
  cursor: pointer;

  &__content {
    .menu-item:hover {
      background: var(--hover-color);
      transition: background 0.2s ease;
    }
  }

  &__anchor {
    display: inline-flex;
    align-items: center;
  }
}

.profile-button {
  &:hover {
    .profile-avatar {
      transform: scale(1.05);
      transition: transform 0.2s ease;
    }
  }
}

.profile-avatar,
.profile-avatar-large {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
}

.avatar-text {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.avatar-text-large {
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.user-info-header {
  background: linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%);
}
</style>
